import { DatePartsPicker } from "@/components/hr/DatePartsPicker";
import { ExpenseBankSelect } from "@/components/expenses/ExpenseBankSelect";
import {
  PaymentMethodFieldsGroup,
  emptyPaymentMethodDraft,
  paymentMethodUsesBank,
  paymentReferenceForSubmit,
  type PaymentMethodDraft,
} from "@/components/sales/PaymentMethodFields";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { formatCurrency, getActiveCompanyCurrency } from "@/lib/currencies/currenciesStore";
import { pkrAmountShortLabel } from "@/lib/formatPkrAmount";
import { fetchBanks, type BankOption } from "@/lib/sales/banksStore";
import type { CancelSalePayload } from "@/lib/sales/saleInvoiceStore";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { Ban, Calculator, CheckCircle2, Info, Loader2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

type RefundMode = "full" | "partial" | "none";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  collected: number;
  onSubmit: (payload: CancelSalePayload) => Promise<void>;
};

function todayIso(): string {
  return format(new Date(), "yyyy-MM-dd");
}

function parseAmount(raw: string): number | null {
  const trimmed = raw.trim().replace(/,/g, "");
  if (!trimmed) return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 100) / 100;
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/** Cancel sale with Full / Partial / No Refund — retained hits Showroom Commission Income (410001). */
export function CancelSaleDialog({ open, onOpenChange, collected, onSubmit }: Props) {
  const collectedSafe = Math.max(0, round2(collected));
  const hasCollections = collectedSafe > 0.009;

  const [refundMode, setRefundMode] = useState<RefundMode>("full");
  const [amountInput, setAmountInput] = useState("");
  const [cancelDate, setCancelDate] = useState(todayIso());
  const [methodDraft, setMethodDraft] = useState<PaymentMethodDraft>(emptyPaymentMethodDraft("cash"));
  const [banks, setBanks] = useState<BankOption[]>([]);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    fetchBanks().then(setBanks).catch(() => {});
    setRefundMode(hasCollections ? "full" : "none");
    setAmountInput(hasCollections ? String(collectedSafe) : "0");
    setCancelDate(todayIso());
    setMethodDraft(emptyPaymentMethodDraft("cash"));
    setNotes("");
    setSaving(false);
    setError(null);
  }, [open, collectedSafe, hasCollections]);

  const refundAmount = useMemo(() => {
    if (!hasCollections || refundMode === "none") return 0;
    if (refundMode === "full") return collectedSafe;
    return parseAmount(amountInput) ?? 0;
  }, [hasCollections, refundMode, collectedSafe, amountInput]);

  const retainedAmount = round2(Math.max(0, collectedSafe - refundAmount));
  const enteredAmountLabel = pkrAmountShortLabel(refundAmount);
  const percentRefunded =
    collectedSafe > 0 ? Math.min(100, Math.round((refundAmount / collectedSafe) * 100)) : 0;
  const needsRefundMethod = refundAmount > 0.009;

  const applyMode = (mode: RefundMode) => {
    setRefundMode(mode);
    setError(null);
    if (mode === "full") {
      setAmountInput(String(collectedSafe));
    } else if (mode === "none") {
      setAmountInput("0");
    } else if (!amountInput.trim() || parseAmount(amountInput) === collectedSafe) {
      const half = round2(collectedSafe / 2);
      setAmountInput(half > 0 ? String(half) : "");
    }
  };

  const save = async () => {
    if (saving) return;

    if (!cancelDate.trim()) {
      setError("Cancel date is required.");
      return;
    }

    if (hasCollections) {
      if (refundMode === "partial") {
        const amount = parseAmount(amountInput);
        if (amount == null) {
          setError("Enter a valid refund amount.");
          return;
        }
        if (amount <= 0.009) {
          setError('Use "No Refund" if you are not returning any money.');
          return;
        }
        if (amount >= collectedSafe - 0.009) {
          setError('Use "Full Refund" to return the entire collected amount.');
          return;
        }
      }

      if (needsRefundMethod) {
        if (!methodDraft.paymentMethod) {
          setError("Select a refund payment method.");
          return;
        }
        if (paymentMethodUsesBank(methodDraft.paymentMethod) && !methodDraft.bankId?.trim()) {
          setError("Select a bank for this refund method.");
          return;
        }
      }
    }

    const finalNotes =
      notes.trim() ||
      (refundMode === "none"
        ? "Customer walk-away — no refund (retained as commission income)"
        : refundMode === "partial"
          ? "Customer walk-away — partial refund"
          : "Customer walk-away — sale cancelled");

    const payload: CancelSalePayload = {
      cancel_date: cancelDate,
      notes: finalNotes,
      refund_amount: round2(refundAmount),
      retained_amount: round2(retainedAmount),
    };

    if (needsRefundMethod) {
      const bankId = methodDraft.bankId?.trim() ? Number(methodDraft.bankId) : null;
      const refundLine = {
        amount: round2(refundAmount),
        payment_method: methodDraft.paymentMethod || "cash",
        bank_id: bankId,
        transaction_reference_no: paymentReferenceForSubmit(methodDraft),
        pay_order_no:
          methodDraft.paymentMethod === "pay_order" ? methodDraft.payOrderNo?.trim() || null : null,
        notes: finalNotes,
      };
      payload.payment_method = refundLine.payment_method;
      payload.bank_id = bankId;
      payload.transaction_reference_no = refundLine.transaction_reference_no;
      payload.pay_order_no = refundLine.pay_order_no;
      payload.refunds = [refundLine];
    }

    setSaving(true);
    setError(null);
    try {
      await onSubmit(payload);
      onOpenChange(false);
    } catch {
      // Caller shows toast; keep dialog open for correction.
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !saving && onOpenChange(next)}>
      <DialogContent className="max-w-3xl w-[95vw] max-h-[90vh] overflow-y-auto p-6">
        <DialogHeader className="pb-3 border-b border-border/50">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-xl bg-rose-500/10 border border-rose-500/20 flex items-center justify-center text-rose-600">
              <Ban className="w-5 h-5" />
            </div>
            <div>
              <DialogTitle className="text-lg font-bold text-foreground">Cancel Sale</DialogTitle>
              <p className="text-xs text-muted-foreground">
                Walk-away cancel — choose full, partial, or no refund. Retained amount posts to Showroom
                Commission Income (410001).
              </p>
            </div>
          </div>
        </DialogHeader>

        <div className="flex items-center justify-between bg-muted/30 border border-border/50 rounded-xl p-3 text-xs">
          <div className="min-w-0">
            <p className="font-bold text-foreground">Customer collections</p>
            <p className="text-[11px] text-muted-foreground uppercase tracking-wider font-semibold">
              Amount already received on this sale
            </p>
          </div>
          <div className="text-right shrink-0 pl-3">
            <p className="text-[10px] uppercase font-semibold text-muted-foreground">Collected</p>
            <p className="font-bold text-primary text-sm">{formatCurrency(collectedSafe)}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 py-2">
          <div className="space-y-4">
            {hasCollections ? (
              <div className="space-y-2">
                <Label className="text-xs font-semibold text-foreground">Refund option</Label>
                <div className="grid grid-cols-3 gap-1.5">
                  {(
                    [
                      { id: "full", label: "Full Refund" },
                      { id: "partial", label: "Partial" },
                      { id: "none", label: "No Refund" },
                    ] as const
                  ).map((opt) => (
                    <button
                      key={opt.id}
                      type="button"
                      disabled={saving}
                      onClick={() => applyMode(opt.id)}
                      className={cn(
                        "text-[11px] font-semibold px-2 py-2 rounded-lg border transition-colors disabled:opacity-50",
                        refundMode === opt.id
                          ? "border-primary/40 bg-primary/10 text-primary"
                          : "border-border/60 bg-background hover:bg-muted/40 text-foreground",
                      )}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}

            {hasCollections && refundMode === "partial" ? (
              <div className="space-y-2">
                <div className="flex min-h-5 items-center justify-between gap-2">
                  <Label htmlFor="cancel-refund-amount" className="text-xs font-semibold text-foreground">
                    Refund amount ({getActiveCompanyCurrency().code}) <span className="text-destructive">*</span>
                  </Label>
                  {enteredAmountLabel ? (
                    <span className="shrink-0 text-xs font-bold tabular-nums text-primary">
                      {enteredAmountLabel}
                    </span>
                  ) : null}
                </div>
                <div className="relative">
                  <Input
                    id="cancel-refund-amount"
                    type="text"
                    inputMode="decimal"
                    value={amountInput}
                    disabled={saving}
                    className="glass-input h-10 pr-12 font-semibold"
                    onChange={(e) => {
                      const nextInput = e.target.value;
                      const nextAmount = parseAmount(nextInput);
                      if (nextAmount !== null && nextAmount > collectedSafe + 0.009) {
                        setError(`Maximum refund is ${formatCurrency(collectedSafe)}.`);
                        return;
                      }
                      setAmountInput(nextInput);
                      setError(null);
                    }}
                    placeholder="0.00"
                  />
                  <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-muted-foreground">
                    {getActiveCompanyCurrency().code}
                  </span>
                </div>
              </div>
            ) : null}

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-foreground">Cancel Date *</Label>
                <DatePartsPicker value={cancelDate} onChange={setCancelDate} disabled={saving} />
              </div>

              {needsRefundMethod ? (
                <PaymentMethodFieldsGroup
                  id="cancel-refund-method"
                  value={methodDraft}
                  onChange={setMethodDraft}
                  banks={banks}
                  disabled={saving}
                  showDetails={false}
                  allowNone={false}
                  label="Refund Method"
                  labelClassName="text-xs font-semibold text-foreground"
                />
              ) : (
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-foreground">Refund Method</Label>
                  <div className="h-10 flex items-center rounded-md border border-border/50 bg-muted/20 px-3 text-xs text-muted-foreground">
                    Not required
                  </div>
                </div>
              )}
            </div>

            {needsRefundMethod && paymentMethodUsesBank(methodDraft.paymentMethod) ? (
              <div className="space-y-1.5 min-w-0 w-full">
                <Label className="text-xs font-semibold text-foreground">Bank</Label>
                <ExpenseBankSelect
                  value={methodDraft.bankId}
                  banks={banks}
                  onChange={(v) => setMethodDraft((d) => ({ ...d, bankId: v }))}
                  disabled={saving}
                  placeholder="Search & select bank"
                  triggerClassName="w-full glass-input h-10"
                />
              </div>
            ) : null}

            {needsRefundMethod && methodDraft.paymentMethod === "online" ? (
              <div className="space-y-1.5 min-w-0 w-full">
                <Label className="text-xs font-semibold text-foreground">Transaction reference no.</Label>
                <Input
                  className="glass-input h-10 w-full"
                  value={methodDraft.transactionReferenceNo}
                  onChange={(e) =>
                    setMethodDraft((d) => ({ ...d, transactionReferenceNo: e.target.value }))
                  }
                  placeholder="e.g. TXN-123456"
                  disabled={saving}
                />
              </div>
            ) : null}

            {needsRefundMethod && methodDraft.paymentMethod === "cheque" ? (
              <div className="space-y-1.5 min-w-0 w-full">
                <Label className="text-xs font-semibold text-foreground">Cheque number</Label>
                <Input
                  className="glass-input h-10 w-full"
                  value={methodDraft.chequeNo ?? ""}
                  onChange={(e) => setMethodDraft((d) => ({ ...d, chequeNo: e.target.value }))}
                  placeholder="e.g. 0048291"
                  disabled={saving}
                />
              </div>
            ) : null}

            {needsRefundMethod && methodDraft.paymentMethod === "pay_order" ? (
              <div className="space-y-1.5 min-w-0 w-full">
                <Label className="text-xs font-semibold text-foreground">Pay order no.</Label>
                <Input
                  className="glass-input h-10 w-full"
                  value={methodDraft.payOrderNo}
                  onChange={(e) => setMethodDraft((d) => ({ ...d, payOrderNo: e.target.value }))}
                  placeholder="e.g. PO-98765"
                  disabled={saving}
                />
              </div>
            ) : null}

            {needsRefundMethod && methodDraft.paymentMethod === "cash_deposit" ? (
              <div className="space-y-1.5 min-w-0 w-full">
                <Label className="text-xs font-semibold text-foreground">Deposit slip no.</Label>
                <Input
                  className="glass-input h-10 w-full"
                  value={methodDraft.depositSlipNo ?? ""}
                  onChange={(e) => setMethodDraft((d) => ({ ...d, depositSlipNo: e.target.value }))}
                  placeholder="e.g. DEP-4412"
                  disabled={saving}
                />
              </div>
            ) : null}

            <div className="space-y-1.5">
              <Label htmlFor="cancel-sale-notes" className="text-xs font-semibold text-foreground">
                Notes
              </Label>
              <Textarea
                id="cancel-sale-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Optional cancel reason"
                rows={2}
                disabled={saving}
              />
            </div>

            {error ? <p className="text-sm text-destructive">{error}</p> : null}
          </div>

          <div className="flex flex-col justify-between rounded-xl border border-rose-500/20 bg-rose-500/5 p-4 space-y-4">
            <div>
              <div className="flex items-center gap-2 pb-3 border-b border-rose-500/10">
                <Calculator className="w-4 h-4 text-rose-600" />
                <h4 className="font-bold text-xs uppercase tracking-wider text-foreground">
                  Cancel Settlement Preview
                </h4>
              </div>

              <div className="space-y-3 pt-3 text-xs">
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Collected</span>
                  <span className="font-bold text-foreground text-sm">{formatCurrency(collectedSafe)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Refund to customer</span>
                  <span
                    className={cn(
                      "font-bold text-sm",
                      refundAmount > 0 ? "text-emerald-600" : "text-foreground",
                    )}
                  >
                    {refundAmount > 0 ? `−${formatCurrency(refundAmount)}` : formatCurrency(0)}
                  </span>
                </div>
                <div className="flex justify-between items-center border-t border-rose-500/10 pt-2">
                  <span className="text-muted-foreground">Retained (410001)</span>
                  <div className="flex items-center gap-1.5">
                    <span className="font-extrabold text-rose-600 text-sm">
                      {formatCurrency(retainedAmount)}
                    </span>
                    {retainedAmount <= 0.009 && hasCollections ? (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 border border-emerald-500/20">
                        <CheckCircle2 className="w-3 h-3" /> Full refund
                      </span>
                    ) : null}
                  </div>
                </div>

                {hasCollections ? (
                  <div className="bg-background/80 rounded-lg border border-rose-500/20 p-3 space-y-2 mt-2">
                    <div className="flex justify-between text-[11px] font-bold">
                      <span className="text-muted-foreground">Refund coverage</span>
                      <span className="text-rose-600">{percentRefunded}%</span>
                    </div>
                    <div className="h-2 w-full bg-rose-500/20 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all bg-rose-500"
                        style={{ width: `${percentRefunded}%` }}
                      />
                    </div>
                    <p className="text-[10px] text-muted-foreground italic text-center pt-0.5">
                      {retainedAmount > 0.009
                        ? `${formatCurrency(retainedAmount)} kept as Showroom Commission Income.`
                        : "Entire collection will be refunded to the customer."}
                    </p>
                  </div>
                ) : (
                  <div className="bg-background/50 rounded-lg border border-border/50 p-3 text-center text-xs text-muted-foreground italic">
                    No collections on this sale — cancel will only reverse books and restore stock.
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground pt-2 border-t border-rose-500/10">
              <Info className="w-3.5 h-3.5 text-rose-600 shrink-0" />
              <span>
                Sale recognition (if posted) is reversed. Original collection vouchers stay for audit.
              </span>
            </div>
          </div>
        </div>

        <DialogFooter className="mt-4 border-t border-border/50 pt-3 gap-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Keep sale
          </Button>
          <Button
            type="button"
            variant="destructive"
            className="gap-2 font-semibold px-5"
            onClick={() => void save()}
            disabled={saving}
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Cancelling…
              </>
            ) : (
              "Cancel sale"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
